// File status tags + notes (V0.4.5): chips shown on any file list, the note under it, and the small editor that sets
// both. Tags and notes are shared — everyone who can see the file sees them; every change is audited.
import { useState } from 'react';
import { write } from '../lib/api';
import { fmtDate } from '../lib/format';
import { STATUS_TAGS, shownStatus, tagOf, type StatusTag, type Taggable } from '../lib/fileTags';
import { errText, useToast } from './kit';
import { C3Busy } from './C3Loader';

export function FileStatus({ f, hasOlder = false, others = [], onOpen }: { f: Taggable; hasOlder?: boolean; others?: Taggable[]; onOpen?: (id: string) => void }) {
  const s = shownStatus(f, hasOlder);
  const t = tagOf(s);
  const by = s === 'superseded' && f.superseded_by ? others.find((o) => o.id === f.superseded_by) : null;
  return (
    <>
      {t ? (
        <span className={`ftag ${t.cls}`} title={!f.status_tag && s ? 'Set automatically from the versions' : undefined}>
          {s === 'superseded' ? (
            <>
              Superseded{by ? ' by → ' : ''}
              {by ? (
                onOpen ? (
                  <button type="button" className="linkbtn" onClick={() => onOpen(by.id)}>
                    {by.original_name}
                  </button>
                ) : (
                  by.original_name
                )
              ) : null}
            </>
          ) : (
            t.label
          )}
        </span>
      ) : null}
      {f.review_flag ? (
        <span className="ftag st-review">
          <i aria-hidden="true" />
          To review
        </span>
      ) : null}
    </>
  );
}

export function FileNote({ f }: { f: Taggable }) {
  if (!f.notes) return null;
  return (
    <div className="fnote">
      “{f.notes}”
      {f.note_by_name || f.note_at ? (
        <span className="fnote-by">
          {' '}
          · {f.note_by_name ?? ''}
          {f.note_by_name && f.note_at ? ' · ' : ''}
          {f.note_at ? fmtDate(f.note_at.slice(0, 10)) : ''}
        </span>
      ) : null}
    </div>
  );
}

/** Inline editor: one status, the To review flag, Superseded by (another file here), and the note. */
export function TagEditor({ f, others, onDone, onCancel }: { f: Taggable; others: Taggable[]; onDone: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [st, setSt] = useState<StatusTag | ''>((f.status_tag as StatusTag) ?? '');
  const [rev, setRev] = useState(Boolean(f.review_flag));
  const [sup, setSup] = useState(f.superseded_by ?? '');
  const [note, setNote] = useState(f.notes ?? '');
  const [busy, setBusy] = useState(false);
  const choices = others.filter((o) => o.id !== f.id);

  async function save() {
    if (st === 'superseded' && !sup) return toast({ tone: 'error', text: 'Pick the file that replaced it' });
    setBusy(true);
    try {
      const values: Record<string, unknown> = { status_tag: st || null, review_flag: rev, superseded_by: st === 'superseded' ? sup : null, notes: note.trim() || null };
      const parts = [st ? tagOf(st)?.label.replace('✓ ', '') : 'no status', rev ? 'to review' : null, note.trim() !== (f.notes ?? '') ? 'note' : null].filter(Boolean).join(', ');
      await write('files', 'update', { id: f.id, values, summary: `Tagged ${f.original_name}: ${parts}` });
      onDone();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tag-edit" role="group" aria-label={`Tag and note for ${f.original_name}`}>
      <div className="tag-choices" role="radiogroup" aria-label="Status">
        <button type="button" role="radio" aria-checked={st === ''} className={`ftag st-none ${st === '' ? 'on' : ''}`} onClick={() => setSt('')}>
          No status
        </button>
        {STATUS_TAGS.map((t) => (
          <button key={t.key} type="button" role="radio" aria-checked={st === t.key} className={`ftag ${t.cls} ${st === t.key ? 'on' : ''}`} onClick={() => setSt(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {st === 'superseded' ? (
        <label className="tag-sup">
          Replaced by
          <select value={sup} onChange={(e) => setSup(e.target.value)}>
            <option value="">— pick the newer file —</option>
            {choices.map((o) => (
              <option key={o.id} value={o.id}>
                {o.original_name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="tag-rev">
        <input type="checkbox" checked={rev} onChange={(e) => setRev(e.target.checked)} /> To review — flag it for the PMs
      </label>
      <label className="tag-note">
        Note
        <textarea rows={2} value={note} placeholder="e.g. AAPI flagged painting % on level 2 — check before signing" onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="tag-foot">
        <span className="muted small grow">Shared with everyone who can see this file · kept in History</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void save()}>
          {busy ? <C3Busy text="Saving…" accent /> : 'Save'}
        </button>
      </div>
    </div>
  );
}
